import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Eye, Edit2, ExternalLink, Shield } from 'lucide-react';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

interface Statement {
  id: number;
  financial_year: string;
  status: string;
  content: string;
  board_approved: boolean;
  board_approval_date: string;
  director_name: string;
  director_signature_date: string;
  publication_url: string;
  publication_date: string;
  gov_registry_submitted: boolean;
  gov_registry_date: string;
  supply_chain_summary: string;
  created_at: string;
  updated_at: string;
}

interface DashboardStats {
  total_statements: number;
  current_status: string;
  applicable: boolean;
  turnover: number;
  turnover_threshold: number;
  supply_chain_vendors: number;
  high_risk_countries: number;
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  in_review: 'bg-blue-100 text-blue-800',
  approved: 'bg-purple-100 text-purple-800',
  published: 'bg-green-100 text-green-800',
};

const WORKFLOW_STEPS = ['draft', 'in_review', 'approved', 'published'];

const EMPTY_FORM = {
  financial_year: '',
  content: '',
  supply_chain_summary: '',
};

const ModernSlavery: React.FC = () => {
  const [statements, setStatements] = useState<Statement[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState<Statement | null>(null);
  const [viewModal, setViewModal] = useState<Statement | null>(null);
  const [approvalModal, setApprovalModal] = useState<Statement | null>(null);
  const [publishModal, setPublishModal] = useState<Statement | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState({ content: '', supply_chain_summary: '' });
  const [approvalForm, setApprovalForm] = useState({ director_name: '', board_approval_date: '' });
  const [publishForm, setPublishForm] = useState({ publication_url: '', publication_date: '', gov_registry_submitted: false });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [listRes, statsRes] = await Promise.all([
        fetch('/api/compliance/modern-slavery', { headers: authHeaders() }).then(r => r.json()),
        fetch('/api/compliance/modern-slavery/dashboard', { headers: authHeaders() }).then(r => r.json()),
      ]);
      setStatements(listRes.statements || []);
      setStats(statsRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.financial_year.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/compliance/modern-slavery', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to create');
      setCreateModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateContent = async () => {
    if (!editModal) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/compliance/modern-slavery/${editModal.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to update');
      setEditModal(null);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusAdvance = async (statement: Statement) => {
    const nextIdx = WORKFLOW_STEPS.indexOf(statement.status) + 1;
    if (nextIdx >= WORKFLOW_STEPS.length) return;
    const nextStatus = WORKFLOW_STEPS[nextIdx];

    if (nextStatus === 'approved') {
      setApprovalModal(statement);
      setApprovalForm({ director_name: statement.director_name || '', board_approval_date: '' });
      return;
    }
    if (nextStatus === 'published') {
      setPublishModal(statement);
      setPublishForm({ publication_url: statement.publication_url || '', publication_date: '', gov_registry_submitted: false });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`/api/compliance/modern-slavery/${statement.id}/status`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to update status');
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!approvalModal) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/compliance/modern-slavery/${approvalModal.id}/approve`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          status: 'approved',
          board_approved: true,
          ...approvalForm,
          director_signature_date: approvalForm.board_approval_date,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to approve');
      setApprovalModal(null);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async () => {
    if (!publishModal) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/compliance/modern-slavery/${publishModal.id}/publish`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          status: 'published',
          ...publishForm,
          ...(publishForm.gov_registry_submitted ? { gov_registry_date: new Date().toISOString().split('T')[0] } : {}),
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to publish');
      setPublishModal(null);
      setSuccess('Statement published successfully.');
      setTimeout(() => setSuccess(''), 4000);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-7 h-7 text-purple-600" />
          <h1 className="text-2xl font-bold text-gray-900">Modern Slavery Compliance</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} />
          </button>
          <button onClick={() => { setForm(EMPTY_FORM); setCreateModal(true); }} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <Plus size={16} /> New Statement
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}
      {success && <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">{success}</div>}

      {/* Applicability Banner */}
      {stats && (
        <div className={`border rounded-xl p-4 ${stats.applicable ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-start gap-3">
            <Shield size={20} className={stats.applicable ? 'text-amber-600' : 'text-green-600'} />
            <div>
              <p className={`text-sm font-medium ${stats.applicable ? 'text-amber-800' : 'text-green-800'}`}>
                {stats.applicable
                  ? 'Your organisation meets the Modern Slavery Act 2015 threshold'
                  : 'Your organisation may not meet the Modern Slavery Act 2015 threshold'}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Annual turnover: {stats.turnover ? `GBP ${stats.turnover.toLocaleString()}` : 'Not set'} | Threshold: GBP {(stats.turnover_threshold || 36000000).toLocaleString()}
              </p>
              {stats.applicable && (
                <p className="text-xs text-amber-700 mt-1">
                  You are required to publish an annual modern slavery statement on your website and submit it to the UK government registry.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Statements', value: stats.total_statements, color: 'text-gray-900' },
            { label: 'Current Status', value: stats.current_status || 'None', color: 'text-blue-600', isText: true },
            { label: 'Supply Chain Vendors', value: stats.supply_chain_vendors, color: 'text-gray-600' },
            { label: 'High Risk Countries', value: stats.high_risk_countries, color: 'text-orange-600' },
          ].map(({ label, value, color, isText }) => (
            <div key={label} className="bg-white border rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className={`${isText ? 'text-lg capitalize' : 'text-2xl'} font-bold ${color}`}>
                {typeof value === 'string' ? value.replace(/_/g, ' ') : value}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Workflow Visualization */}
      <div className="bg-white border rounded-xl p-4">
        <p className="text-xs text-gray-500 mb-2 font-medium uppercase">Statement Workflow</p>
        <div className="flex items-center gap-1 text-xs">
          {WORKFLOW_STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <span className={`px-3 py-1.5 rounded-full font-medium capitalize whitespace-nowrap ${statusColors[s]}`}>
                {s.replace(/_/g, ' ')}
              </span>
              {i < WORKFLOW_STEPS.length - 1 && <span className="text-gray-300 mx-1">&#8594;</span>}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Statements List */}
      <div className="bg-white border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : statements.length === 0 ? (
          <div className="py-12 text-gray-500">No modern slavery statements yet.</div>
        ) : (
          <div className="divide-y">
            {statements.map(s => (
              <div key={s.id} className="p-5 hover:bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-gray-900">FY {s.financial_year}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[s.status] || 'bg-gray-100 text-gray-700'}`}>
                      {s.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setViewModal(s)} className="p-1.5 text-gray-400 hover:text-gray-600">
                      <Eye size={16} />
                    </button>
                    {s.status === 'draft' && (
                      <button onClick={() => { setEditModal(s); setEditForm({ content: s.content || '', supply_chain_summary: s.supply_chain_summary || '' }); }} className="p-1.5 text-gray-400 hover:text-gray-600">
                        <Edit2 size={16} />
                      </button>
                    )}
                    {s.publication_url && (
                      <a href={s.publication_url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-gray-400 hover:text-gray-600">
                        <ExternalLink size={16} />
                      </a>
                    )}
                    {WORKFLOW_STEPS.indexOf(s.status) < WORKFLOW_STEPS.length - 1 && (
                      <button
                        onClick={() => handleStatusAdvance(s)}
                        disabled={submitting}
                        className="px-3 py-1.5 text-xs text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        Advance to {WORKFLOW_STEPS[WORKFLOW_STEPS.indexOf(s.status) + 1]?.replace(/_/g, ' ')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Board Approved</p>
                    <p className="font-medium">{s.board_approved ? `Yes (${s.board_approval_date ? new Date(s.board_approval_date).toLocaleDateString() : '—'})` : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Director</p>
                    <p className="font-medium">{s.director_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Published</p>
                    <p className="font-medium">{s.publication_date ? new Date(s.publication_date).toLocaleDateString() : '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Gov Registry</p>
                    <p className="font-medium">{s.gov_registry_submitted ? `Submitted (${s.gov_registry_date ? new Date(s.gov_registry_date).toLocaleDateString() : '—'})` : 'Not submitted'}</p>
                  </div>
                </div>

                {s.supply_chain_summary && (
                  <div className="mt-3 text-sm">
                    <p className="text-gray-500 text-xs mb-1">Supply Chain Due Diligence Summary</p>
                    <p className="text-gray-700 line-clamp-2">{s.supply_chain_summary}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Statement Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">New Modern Slavery Statement</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Financial Year *</label>
                <input value={form.financial_year} onChange={e => setForm(f => ({ ...f, financial_year: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. 2025/26" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statement Content</label>
                <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={8} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Enter the modern slavery statement text..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Supply Chain Due Diligence Summary</label>
                <textarea value={form.supply_chain_summary} onChange={e => setForm(f => ({ ...f, supply_chain_summary: e.target.value }))} rows={4} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Summarise supply chain due diligence steps..." />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleCreate} disabled={submitting || !form.financial_year.trim()} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create Statement'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Content Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Edit Statement - FY {editModal.financial_year}</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statement Content</label>
                <textarea value={editForm.content} onChange={e => setEditForm(f => ({ ...f, content: e.target.value }))} rows={12} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Supply Chain Due Diligence Summary</label>
                <textarea value={editForm.supply_chain_summary} onChange={e => setEditForm(f => ({ ...f, supply_chain_summary: e.target.value }))} rows={4} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setEditModal(null)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleUpdateContent} disabled={submitting} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Modern Slavery Statement - FY {viewModal.financial_year}</h2>
                <button onClick={() => setViewModal(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
              </div>
              <div className="prose prose-sm max-w-none">
                {viewModal.content ? (
                  <div className="whitespace-pre-wrap text-sm text-gray-700">{viewModal.content}</div>
                ) : (
                  <p className="text-gray-400 italic">No content yet.</p>
                )}
              </div>
              {viewModal.supply_chain_summary && (
                <div className="border-t pt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Supply Chain Due Diligence</h3>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{viewModal.supply_chain_summary}</p>
                </div>
              )}
              <div className="flex justify-end pt-2">
                <button onClick={() => setViewModal(null)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {approvalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Board Approval</h2>
              <p className="text-sm text-gray-500">Approve the modern slavery statement for FY {approvalModal.financial_year}</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Director Name *</label>
                <input value={approvalForm.director_name} onChange={e => setApprovalForm(f => ({ ...f, director_name: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Board Approval Date *</label>
                <input type="date" value={approvalForm.board_approval_date} onChange={e => setApprovalForm(f => ({ ...f, board_approval_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setApprovalModal(null)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleApprove} disabled={submitting || !approvalForm.director_name || !approvalForm.board_approval_date} className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50">
                  {submitting ? 'Approving...' : 'Approve Statement'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publish Modal */}
      {publishModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Publish Statement</h2>
              <p className="text-sm text-gray-500">Publish the modern slavery statement for FY {publishModal.financial_year}</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website Publication URL *</label>
                <input value={publishForm.publication_url} onChange={e => setPublishForm(f => ({ ...f, publication_url: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="https://yourcompany.com/modern-slavery" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Publication Date *</label>
                <input type="date" value={publishForm.publication_date} onChange={e => setPublishForm(f => ({ ...f, publication_date: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={publishForm.gov_registry_submitted} onChange={e => setPublishForm(f => ({ ...f, gov_registry_submitted: e.target.checked }))} />
                Submitted to UK Government Modern Slavery Statement Registry
              </label>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setPublishModal(null)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handlePublish} disabled={submitting || !publishForm.publication_url || !publishForm.publication_date} className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50">
                  {submitting ? 'Publishing...' : 'Publish Statement'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernSlavery;
